import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { uploadToS3 } from "./uploadToS3"
import AnalyzeOrderModal from "@/components/analyzeOrderOnPage/AnalyzeOrderOnPageModal"
import PlaneOrderOnPageModal from "@/components/analyzeOrderOnPage/PlanOrderOnPageModal"
import { createPortal } from "react-dom"
import { generateSessionId, loadSavedRoute } from "@/lib/saveRoutes"
import { SavedRouteState } from "@/lib/routeStateUtils"
import { toast } from "sonner"
import AdHocCommands, { AvoidType, LoadType } from "@/api/adHocCommands"
import { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { changeActiveTab, selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import html2canvas from "html2canvas"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { XmppContext } from "@/stream/xmppClient"
import Route2gFromFormParser from "@/lib/route2gFromFormParser"
import { parseRouteMetaData } from "@/hooks/useExtractDataFromOrder"
import {
  ANY_FORMAT_ORDERS_FLAG,
  getOrderFileAccept,
  isSupportedOrderFile,
} from "@/lib/orderFileSupport"
import TabbedPlanOrderOnPageModal from "@/components/analyzeOrderOnPage/TabbedPlanOrderOnPageModal"
import { usePlanRouteTabs } from "@/components/analyzeOrderOnPage/usePlanRouteTabs"
import { useSharedRouteLink } from "@/components/analyzeOrderOnPage/useSharedRouteLink"
import {
  PLAN_ROUTE_TABS_FLAG,
  snapshotFromSavedRoute,
  tabsStorageKey,
} from "@/components/analyzeOrderOnPage/planRouteTabs"

// Helper function to convert text to image file
export const convertTextToImage = async (text: string): Promise<File> => {
  // Create a temporary div element with the text
  const tempDiv = document.createElement("div")
  tempDiv.style.position = "absolute"
  tempDiv.style.left = "-9999px"
  tempDiv.style.top = "-9999px"
  tempDiv.style.width = "800px"
  tempDiv.style.padding = "20px"
  tempDiv.style.fontSize = "16px"
  tempDiv.style.fontFamily = "Arial, sans-serif"
  tempDiv.style.lineHeight = "1.5"
  tempDiv.style.color = "#000"
  tempDiv.style.backgroundColor = "#fff"
  tempDiv.style.border = "2px solid #ccc"
  tempDiv.style.borderRadius = "8px"
  tempDiv.style.whiteSpace = "pre-wrap"
  tempDiv.style.wordWrap = "break-word"
  tempDiv.textContent = text

  // Add to document temporarily
  document.body.appendChild(tempDiv)

  try {
    // Convert to canvas
    const canvas = await html2canvas(tempDiv, {
      backgroundColor: "#ffffff",
      scale: 2, // Higher quality
      useCORS: true,
    })

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), "image/png")
    })

    // Create File object
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
    const file = new File([blob], `text-order-${timestamp}.png`, {
      type: "image/png",
    })

    return file
  } finally {
    // Clean up
    document.body.removeChild(tempDiv)
  }
}

function PlanOrder() {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setUploadedFile] = useState<File | null>(null)
  const [extractedData, setExtractedData] = useState<any>(null)
  const [error, setError] = useState<string>("")
  const [fileInputValue, setFileInputValue] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [sharedRouteState, setSharedRouteState] = useState<any>(null)
  const [sharedDocumentStep, setSharedDocumentStep] = useState<string>("verify")

  const { client, myJid } = useContext(XmppContext)
  const anyFormatEnabled = Boolean(
    useFeatureFlagEnabled(ANY_FORMAT_ORDERS_FLAG),
  )
  const routeTabsFlag = useFeatureFlagEnabled(PLAN_ROUTE_TABS_FLAG)
  const routeTabsEnabled = Boolean(routeTabsFlag)
  const routeTabs = usePlanRouteTabs(tabsStorageKey(myJid?.bare().toString()))
  const { openRoute } = routeTabs

  const [isAnalyzeOrderOpen, setIsAnalyzeOrderOpen] = useState(false)
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false)

  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(
    undefined,
  )
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileUrl, setFileUrl] = useState<string | null>(null)

  const [selectedAvoidOptions, setSelectedAvoidOptions] = useState<AvoidType[]>(
    [],
  )

  const [selectedDriver, setSelectedDriver] = useState<
    SelectedDriverLocation | undefined
  >(undefined)

  const [selectedLoadType, setSelectedLoadType] = useState<LoadType[]>([])

  const [selectedVehicleType, setSelectedVehicleType] =
    useState<VehicleType>("tir")

  // Text order dialog state
  const [isTextOrderDialogOpen, setIsTextOrderDialogOpen] = useState(false)
  const [textOrderContent, setTextOrderContent] = useState("")
  const [isProcessingTextOrder, setIsProcessingTextOrder] = useState(false)

  useEffect(() => {
    setCurrentSessionId(generateSessionId())
  }, [])

  const { activeTab } = useAppSelector(selectTabs)
  const active = activeTab === TAB_TYPES.PLAN_ORDER

  useLayoutEffect(() => {
    if (activeTab === TAB_TYPES.PLAN_ORDER) {
      setIsPlanModalOpen(true)
    } else {
      setIsPlanModalOpen(false)
    }
  }, [activeTab])

  const resetComponentState = () => {
    setIsProcessing(false)
    setUploadedFile(null)
    setExtractedData(null)
    setCurrentSessionId(undefined)
    setError("")
    setFileInputValue("")
    setSharedRouteState(null)
    setSharedDocumentStep("verify")
    setIsAnalyzeOrderOpen(false)
    setIsPlanModalOpen(true)
    setFileName(null)
    setFileUrl(null)
    setSelectedDriver(undefined)
    setSelectedAvoidOptions([])
    setSelectedLoadType([])
    setSelectedVehicleType("tir")
    setIsTextOrderDialogOpen(false)
    setTextOrderContent("")
    setIsProcessingTextOrder(false)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
    // Reset file input value to allow selecting the same file again
    setFileInputValue("")
  }

  const handleFileSelect = async (file: File) => {
    if (!isSupportedOrderFile(file, anyFormatEnabled)) {
      setError(
        anyFormatEnabled
          ? t("planOrder.fileErrors.unsupportedFormat")
          : t("planOrder.fileErrors.imageOrPdf"),
      )
      return
    }

    if (!client || !myJid) {
      setError(t("planOrder.fileErrors.noConnection"))
      return
    }

    setUploadedFile(file)
    setIsProcessing(true)
    setError("")
    setFileName(file.name)

    // Show analyze modal immediately
    setIsPlanModalOpen(false)
    setIsAnalyzeOrderOpen(true)

    try {
      // Upload to S3
      const { getUrl, fileName: s3FileName } = await uploadToS3(file)

      setFileUrl(getUrl)
      setFileName(s3FileName)

      // Use XMPP adhoc command to extract route from order
      const uploadedFile = {
        url: getUrl,
        name: s3FileName,
        file: file,
      }

      const result = await AdHocCommands.extractRouteFromOrders(
        client,
        myJid.toString(),
        [uploadedFile],
      )

      const commandResponse = result?.getChild("command")
      if (!commandResponse) {
        throw new Error("No command response from XMPP server")
      }
      const routeMetaData = parseRouteMetaData(commandResponse)
      const route2g = Route2gFromFormParser.parse(commandResponse)

      if (!routeMetaData || !route2g) {
        throw new Error("Failed to parse route data from response")
      }

      // Transform stops to the expected format. Keep the full geocoded
      // location (lat/lng, street, locality, country) — create_navi_order
      // sends stops inline to Marcos and needs the structured address fields.
      const data = {
        ...routeMetaData,
        stops: route2g.stops.map((stop: any) => {
          return {
            action: stop.action,
            scheduled_arrival_time: stop.scheduled_arrival_time,
            scheduled_hour_min: stop.scheduled_hour_min,
            scheduled_hour_max: stop.scheduled_hour_max,
            location: {
              ...stop.location,
              formattedAddress: stop.location?.formattedAddress || "",
            },
            action_reference_number: stop.action_reference_number,
            pallet_count: stop.pallet_count,
            weight_kg: stop.weight_kg,
          }
        }),
      }

      setIsProcessing(false)
      setExtractedData(data)
      window.analytics?.track("OrderOnPage_MetaDataExtracted", {
        metaData: data,
      })
    } catch (err: unknown) {
      setIsAnalyzeOrderOpen(false)
      console.error("Error processing file:", err)
      window.analytics?.track("OrderOnPage_MetaDataExtractingFailed", {
        error: err,
      })
      setError(t("planOrder.fileErrors.processingFailed"))
      setIsProcessing(false)
    }
  }

  const handleSwitchToAnalyzeOrder = useCallback(
    ({
      avoid,
      loadType,
      vehicleType,
      selectedDriver,
      file,
    }: {
      avoid: AvoidType[]
      loadType: LoadType[]
      vehicleType: VehicleType
      selectedDriver: SelectedDriverLocation | undefined
      // Already-picked document (drag & drop) — process it directly instead of
      // opening the file picker.
      file?: File
    }) => {
      setSelectedDriver(selectedDriver)
      setSelectedAvoidOptions(avoid)
      setSelectedLoadType(loadType)
      setSelectedVehicleType(vehicleType)
      if (file) {
        handleFileSelect(file)
      } else {
        fileInputRef.current?.click()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Handle opening text order dialog
  const handleOpenTextOrder = useCallback(
    ({
      avoid,
      loadType,
      vehicleType,
      selectedDriver,
    }: {
      avoid: AvoidType[]
      loadType: LoadType[]
      vehicleType: VehicleType
      selectedDriver: SelectedDriverLocation | undefined
    }) => {
      setSelectedDriver(selectedDriver)
      setSelectedAvoidOptions(avoid)
      setSelectedLoadType(loadType)
      setSelectedVehicleType(vehicleType)
      setIsTextOrderDialogOpen(true)
    },
    [],
  )

  // Handle closing text order dialog
  const handleCloseTextOrder = useCallback(() => {
    setIsTextOrderDialogOpen(false)
    setTextOrderContent("")
    setIsProcessingTextOrder(false)
  }, [])

  // Handle dialog open/close changes (for ESC, clicking outside, etc.)
  const handleTextOrderDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Clear content when closing dialog by any method
      setTextOrderContent("")
      setIsProcessingTextOrder(false)
    }
    setIsTextOrderDialogOpen(open)
  }, [])

  // Handle submitting text order
  const handleSubmitTextOrder = useCallback(async () => {
    if (!textOrderContent.trim()) {
      toast.warning(t("planOrder.textOrder.empty"))
      return
    }

    setIsProcessingTextOrder(true)
    try {
      const text = textOrderContent.trim()
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
      // With any-format-orders the backend accepts plain text directly;
      // otherwise fall back to rasterizing the text to a PNG.
      const orderFile = anyFormatEnabled
        ? new File([text], `text-order-${timestamp}.txt`, {
            type: "text/plain",
          })
        : await convertTextToImage(text)

      // Close dialog first
      handleCloseTextOrder()

      // Process the file using existing handleFileSelect
      handleFileSelect(orderFile)
    } catch (error) {
      console.error("Error processing text order:", error)
      toast.error(t("planOrder.textOrder.failed"))
      setIsProcessingTextOrder(false)
    }
  }, [
    textOrderContent,
    handleCloseTextOrder,
    handleFileSelect,
    anyFormatEnabled,
    t,
  ])

  const openRouteState = useCallback(
    (savedRoute: SavedRouteState) => {
      if (
        savedRoute.extractedData &&
        savedRoute.fileName &&
        savedRoute.fileUrl
      ) {
        setSharedRouteState(savedRoute)
        setCurrentSessionId(generateSessionId())
        setExtractedData(savedRoute.extractedData)
        setFileName(savedRoute.fileName)
        setFileUrl(savedRoute.fileUrl)
        setSharedDocumentStep("map")
        setIsAnalyzeOrderOpen(true)
      } else if (routeTabsEnabled) {
        openRoute(snapshotFromSavedRoute(savedRoute))
        setIsPlanModalOpen(true)
      } else {
        setSharedRouteState(savedRoute)
        setCurrentSessionId(generateSessionId())
        setIsPlanModalOpen(true)
      }
    },
    [routeTabsEnabled, openRoute],
  )

  const handleLoadSavedRoute = useCallback(
    (routeId: string) => {
      if (!routeId) return

      const savedRoute = loadSavedRoute(routeId)
      if (savedRoute) {
        openRouteState(savedRoute)
        toast(t("planOrder.routeToasts.loaded", { name: savedRoute?.name }))
        window.analytics?.track("OrderOnPage_AutoSavedRouteLoaded", {
          routeId,
          routeName: savedRoute?.name,
          stopsCount: savedRoute?.temporaryRoute.stops.length,
        })
      } else {
        console.warn("Shared route not found or expired:", routeId)
        window.analytics?.track("OrderOnPage_SharedRouteNotFound", {
          routeId,
        })
      }
    },
    [openRouteState, t],
  )

  useSharedRouteLink(openRouteState, routeTabsFlag !== undefined)

  const dispatch = useAppDispatch()

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={getOrderFileAccept(anyFormatEnabled)}
        value={fileInputValue}
        onChange={handleFileInputChange}
        className="hidden"
      />
      {active &&
        isAnalyzeOrderOpen &&
        createPortal(
          <AnalyzeOrderModal
            isProcessing={isProcessing}
            fileUrl={fileUrl}
            fileName={fileName}
            isOpen={isAnalyzeOrderOpen}
            onClose={() => {
              setIsAnalyzeOrderOpen(false)
              setIsPlanModalOpen(false)
              setExtractedData(null)
              setUploadedFile(null)
              setError("")
              setFileInputValue("")
              setCurrentSessionId(generateSessionId())
              setSharedRouteState(null)
              setSharedDocumentStep("verify")
              dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
            }}
            initialAvoidOptions={selectedAvoidOptions}
            initialLoadType={selectedLoadType}
            initialVehicleType={selectedVehicleType}
            initialDriver={selectedDriver}
            onLoadSavedRoute={handleLoadSavedRoute}
            extractedData={extractedData}
            initialStep={sharedDocumentStep}
            sharedRouteState={sharedRouteState}
            onReset={resetComponentState}
          />,
          document.body,
        )}

      {active &&
        isPlanModalOpen &&
        routeTabsEnabled &&
        createPortal(
          <TabbedPlanOrderOnPageModal
            key={routeTabs.plannerKey}
            tabs={routeTabs}
            isOpen={isPlanModalOpen}
            onLoadSavedRoute={handleLoadSavedRoute}
            onSwitchToAnalyzeOrder={handleSwitchToAnalyzeOrder}
            onSwitchToTextOrder={handleOpenTextOrder}
            sessionId={currentSessionId}
            onReset={resetComponentState}
            onClose={() => {
              // The tabs stay as they are for the next time the planner opens.
              setIsPlanModalOpen(false)
              dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
              setCurrentSessionId(generateSessionId())
              setSharedRouteState(null)
            }}
          />,
          document.body,
        )}

      {active &&
        isPlanModalOpen &&
        !routeTabsEnabled &&
        createPortal(
          <PlaneOrderOnPageModal
            isOpen={isPlanModalOpen}
            onLoadSavedRoute={handleLoadSavedRoute}
            onSwitchToAnalyzeOrder={handleSwitchToAnalyzeOrder}
            onSwitchToTextOrder={handleOpenTextOrder}
            sessionId={currentSessionId}
            sharedRouteState={sharedRouteState}
            onReset={resetComponentState}
            onClose={() => {
              setIsPlanModalOpen(false)
              dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
              setCurrentSessionId(generateSessionId())
              setSharedRouteState(null)
            }}
          />,
          document.body,
        )}

      <Dialog
        open={isTextOrderDialogOpen}
        onOpenChange={handleTextOrderDialogOpenChange}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("planOrder.textOrder.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("planOrder.textOrder.description")}
            </p>
            <Textarea
              placeholder={t("planOrder.textOrder.placeholder")}
              value={textOrderContent}
              onChange={(e) => setTextOrderContent(e.target.value)}
              rows={15}
              className="min-h-[400px] text-base"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseTextOrder}
              disabled={isProcessingTextOrder}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="ghost"
              className="bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
              onClick={handleSubmitTextOrder}
              disabled={isProcessingTextOrder || !textOrderContent.trim()}
            >
              {isProcessingTextOrder
                ? t("planOrder.textOrder.processing")
                : t("planOrder.textOrder.analyze")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PlanOrder
